class ZafariProduct {
  constructor(root) {
    this.root = root;
    this.variants = JSON.parse(root.querySelector('[data-z-product-variants]').textContent);
    this.optionGroups = [...root.querySelectorAll('[data-z-option-index]')];
    this.variantInput = root.querySelector('[data-z-variant-id]');
    this.submit = root.querySelector('[data-z-add-to-cart]');
    this.stickySubmit = root.querySelector('[data-z-sticky-add]');
    this.quantity = root.querySelector('[name="quantity"]');
    this.sellingPlan = root.querySelector('[data-z-selling-plan]');
    this.optionGroups.forEach((group) => group.addEventListener('change', () => this.onOptionsChange()));
    this.initSticky();
    this.updateOptionAvailability();
    const initialVariant = this.variants.find((variant) => String(variant.id) === this.variantInput.value);
    if (initialVariant?.featuredMediaId) this.activateMedia(initialVariant.featuredMediaId, false);
  }

  selectedOptions() {
    return this.optionGroups.map((group) => group.querySelector('input:checked')?.value || '');
  }

  onOptionsChange() {
    const selected = this.selectedOptions();
    const variant = this.variants.find((item) => item.options.every((value, index) => value === selected[index]));
    this.optionGroups.forEach((group, index) => {
      const output = group.querySelector('[data-z-option-selected]');
      if (output) output.textContent = selected[index];
    });
    this.updateOptionAvailability();
    this.updateVariant(variant);
  }

  updateOptionAvailability() {
    const selected = this.selectedOptions();
    this.optionGroups.forEach((group, optionIndex) => {
      group.querySelectorAll('[data-z-option-value]').forEach((input) => {
        const proposed = [...selected];
        proposed[optionIndex] = input.value;
        const available = this.variants.some((variant) => variant.available && variant.options[optionIndex] === input.value && variant.options.slice(0, optionIndex).every((value, index) => value === proposed[index]));
        input.disabled = !available && !input.checked;
        input.nextElementSibling?.classList.toggle('is-unavailable', !available);
      });
    });
  }

  updateVariant(variant) {
    const available = Boolean(variant?.available);
    this.variantInput.value = variant?.id || '';
    this.submit.disabled = !available;
    this.submit.querySelector('[data-z-add-label]').textContent = available ? 'Add to bag' : 'Sold out';
    if (this.stickySubmit) {
      this.stickySubmit.disabled = !available;
      this.stickySubmit.textContent = available ? 'Add to bag' : 'Sold out';
    }
    this.root.querySelector('[data-z-availability]').textContent = available ? 'Available' : 'Currently unavailable';
    if (!variant) return;

    this.root.querySelector('[data-z-current-price]').innerHTML = variant.price;
    const compare = this.root.querySelector('[data-z-compare-price]');
    compare.hidden = !variant.compareAt;
    compare.innerHTML = variant.compareAt || '';
    const unit = this.root.querySelector('[data-z-unit-price]');
    unit.hidden = !variant.unitPrice;
    unit.innerHTML = variant.unitPrice || '';
    const stickyPrice = this.root.querySelector('[data-z-sticky-price]');
    if (stickyPrice) stickyPrice.innerHTML = variant.price;

    if (this.quantity) {
      this.quantity.min = variant.quantityMin || 1;
      this.quantity.step = variant.quantityStep || 1;
      if (variant.quantityMax) this.quantity.max = variant.quantityMax;
      else this.quantity.removeAttribute('max');
      const currentQuantity = Number(this.quantity.value);
      const minimumQuantity = Number(this.quantity.min);
      const maximumQuantity = this.quantity.max ? Number(this.quantity.max) : null;
      const quantityStep = Number(this.quantity.step);
      const isInvalidStep = Number.isFinite(currentQuantity) && (currentQuantity - minimumQuantity) % quantityStep !== 0;
      if (!Number.isFinite(currentQuantity) || currentQuantity < minimumQuantity || (maximumQuantity && currentQuantity > maximumQuantity) || isInvalidStep) {
        this.quantity.value = this.quantity.min;
      }
    }
    if (this.sellingPlan) {
      [...this.sellingPlan.options].forEach((option) => {
        if (!option.dataset.variantId) return;
        const relevant = option.dataset.variantId === String(variant.id);
        option.hidden = !relevant;
        option.disabled = !relevant;
      });
      if (this.sellingPlan.selectedOptions[0]?.disabled) {
        const firstRelevant = [...this.sellingPlan.options].findIndex((option) => !option.disabled);
        this.sellingPlan.selectedIndex = firstRelevant;
      }
    }
    if (variant.featuredMediaId) this.activateMedia(variant.featuredMediaId, true);
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  activateMedia(mediaId, shouldScroll) {
    const media = this.root.querySelector(`[data-z-media-id="${mediaId}"]`);
    const rail = media?.closest('[data-z-media-rail]');
    if (!media || !rail) return;
    rail.querySelectorAll('[data-z-media-id]').forEach((item) => item.classList.toggle('is-active', item === media));
    if (!shouldScroll) return;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    if (window.matchMedia('(max-width: 63.99rem)').matches) rail.scrollTo({ left: media.offsetLeft, behavior });
    else media.scrollIntoView({ behavior, block: 'nearest' });
  }

  initSticky() {
    const sticky = this.root.querySelector('[data-z-product-sticky]');
    if (!sticky || !this.submit || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(([entry]) => sticky.classList.toggle('is-visible', !entry.isIntersecting), { threshold: 0 });
    observer.observe(this.submit);
  }
}

const initZafariProducts = (scope = document) => {
  scope.querySelectorAll('[data-z-product]').forEach((root) => {
    if (!root.zafariProduct) root.zafariProduct = new ZafariProduct(root);
  });
};

class ZafariRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded) return;
    this.dataset.loaded = 'true';
    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html').querySelector('product-recommendations');
        if (html?.innerHTML.trim()) this.innerHTML = html.innerHTML;
      })
      .catch(() => {});
  }
}

if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ZafariRecommendations);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initZafariProducts());
else initZafariProducts();
document.addEventListener('shopify:section:load', (event) => initZafariProducts(event.target));
